import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GoogleConfigurations } from '@shared/models/configurations.interface';
import { API_ENDPOINTS } from '@shared/constants';
import { environment } from '@envs/environment';
import { Observable, take, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MapLoaderService {
  private isLoaded = false;
  private readonly _http = inject(HttpClient);
  private readonly CONFIG_KEY = 'GoogleAPIkeyDispatcher';

  loadMaps() {
    return new Observable<void>(observer => {
      if (this.isLoaded) {
        observer.next();
        observer.complete();
        return;
      }

      // Check if the script is already in the document to avoid duplicates
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        this.isLoaded = true;
        observer.next();
        observer.complete();
        return;
      }

      this.getGoogleAPIConfigurations()
        .pipe(
          catchError(err => {
            observer.error(
              new Error('Failed to fetch API key: ' + err.message)
            );
            return [];
          }),
          tap(configs => {
            const script = document.createElement('script');
            script.setAttribute('type', 'text/javascript');
            script.setAttribute('defer', 'true');
            script.setAttribute('id', 'BC_MAPS');
            script.innerHTML = `
          (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
            key: "${configs.googleKey}",
            v: "weekly"
          });
        `;
            script.onerror = error => {
              observer.error(error);
            };
            document.head.appendChild(script);

            const checkGoogleMaps = () => {
              if (typeof google !== 'undefined' && google.maps) {
                this.isLoaded = true;
                observer.next();
                observer.complete();
              } else {
                setTimeout(checkGoogleMaps, 100); // Check every 100 ms
              }
            };

            checkGoogleMaps();
          })
        )
        .subscribe();
    });
  }

  getGoogleAPIConfigurations() {
    const isLocal = environment.isLocal;
    return this._http
      .get<GoogleConfigurations>(
        `${API_ENDPOINTS.Configurations.GetGoogleAPIConfigurations}${this.CONFIG_KEY}/${isLocal}`
      )
      .pipe(take(1));
  }
}
